import {
  F2fTransactionStatusCode,
  type IAdminTransaction,
  type ITransaction,
} from "@gen/app-query";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link } from "@tanstack/react-router";

import { HeadingContainer } from "~/components/heading_container";
import { LedgerAmount } from "~/components/ledger_amount";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { useConfig } from "~/contexts/config";
import { TransactionMoney } from "~/entities/transaction";

interface ClientSectionProps {
  transactionData: ITransaction | IAdminTransaction;
}

function Title() {
  return (
    <>
      Partner Accrual Net
      <Tooltip
        title={`Gross partner commission minus costs\nabsorbed (e.g. processing costs, instant\npayout fees, or Trustap fees on refunds)`}
        placement="top"
      >
        <PublicSvgIcon name="info" />
      </Tooltip>
    </>
  );
}

export function ClientSection({ transactionData }: ClientSectionProps) {
  const { localization } = useConfig();

  const isAdminTx = "client_site" in transactionData;
  const clientId = transactionData.client_id;
  const transactionMoney = new TransactionMoney(transactionData);
  const clientAccrualNet = transactionMoney.getClientAccrualNet();
  const accrualNetDeltas = transactionMoney
    .getRefunds()
    .filter((refund) => !refund.accrualNetDelta.isZero());
  let clientSite: string | undefined;
  if ("client_site" in transactionData) {
    clientSite = transactionData.client_site;
  }

  let amountLabel = "Expected Amount";
  if (
    transactionData.status === F2fTransactionStatusCode.Funds_released ||
    transactionData.status === F2fTransactionStatusCode.Cancelled ||
    transactionData.status === F2fTransactionStatusCode.Deposit_refunded
  ) {
    amountLabel = "Confirmed Amount";
  }

  return (
    <Stack
      spacing={5}
      sx={{
        p: 3,
        backgroundColor: "primary._states.hover",
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h5SolidDivider"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <PublicSvgIcon name="identity_platform" /> Client
      </Typography>
      <Stack spacing={2}>
        {transactionData.client_accrual_net !== undefined && (
          <Box>
            <HeadingContainer
              borderType="dashed"
              slotProps={{
                title: { fontWeight: "bold", gap: 0.5 },
              }}
              title={<Title />}
              variant="body2"
            />
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">{amountLabel}</Typography>
              <LedgerAmount
                amount={clientAccrualNet}
                data-testid="client-section-client_accrual_net-text"
              />
            </Stack>
            {accrualNetDeltas.map((refund) => (
              <Stack
                key={refund.id}
                flexDirection="row"
                justifyContent="space-between"
              >
                <Typography variant="body2">
                  Refund Delta{" "}
                  {
                    refund.refunded
                      .toLocaleString(localization.locale, {
                        timeZone: localization.timeZone,
                      })
                      .split(", ")[0]
                  }
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  data-testid={`client-section-accrual_net_delta-${refund.id}-text`}
                >
                  {refund.accrualNetDelta.format()}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}
        <Box>
          <Typography variant="body2DashedDivider" sx={{ fontWeight: "bold" }}>
            Client ID
          </Typography>
          {clientId ? (
            isAdminTx ? (
              <MuiLink
                component={Link}
                to={`/admin/clients/${clientId}`}
                data-testid="client-section-id-link"
              >
                {clientId}
              </MuiLink>
            ) : (
              <Typography variant="body2" data-testid="client-section-id-text">
                {clientId}
              </Typography>
            )
          ) : (
            <Typography variant="body2" data-testid="client-section-id-text">
              N/A
            </Typography>
          )}
        </Box>
        {clientSite !== undefined && (
          <Box>
            <Typography
              variant="body2DashedDivider"
              sx={{ fontWeight: "bold" }}
            >
              Client Site
            </Typography>
            <Typography variant="body2" data-testid="client-section-site-text">
              {clientSite || "N/A"}
            </Typography>
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
